// ── The country's banks: the Lease section's Bank dropdown (report 4c63e9a1, opening #6) ─────────
// The reporter: "Choose from a list of Thai banks rather than allow the text box to be filled in." A
// reference list, as COUNTRIES is — the banks a lessor's rent account may be at, never any firm's own
// account — each with its English and its Thai name. The stored answer is the English name, as listed;
// the dropdown shows 'English / Thai' in every language (the names are proper nouns), and the Lease
// Agreement prints the English name and the Thai name in Schedule 3 (server services/leaseDocument.ts).
// Mirror: server/src/services/thaiBanks.ts (the agreement's printing, and a lease document's reading
// before the screen sees it) — keep the two in step.
//
// thaiBankOf finds the entry a bank written any other way means — a lease document's reading ("KASIKORN
// BANK PCL, Maenam Koh Samui Branch"), a bank typed before the box was a choice ("Kasikorn Bank"), a Thai
// name with its branch ("ธนาคารกสิกรไทย สาขาแม่น้ำ") or an abbreviation ("KBank", "SCB") — the branch
// dropped: what follows a comma, a bracket, a dash or 'สาขา' is cut, the words every bank's name carries
// (Bank, PCL, Public Company Limited …, ธนาคาร, จำกัด, มหาชน) are left out, and the words left are matched
// from their START against each entry's names and aliases, so "UOB Bangkok Branch" is UOB, never Bangkok
// Bank. null when nothing matches: such a bank stays as typed.
export type ThaiBank = { en: string; th: string; alias: string[] };
export const THAI_BANKS: ThaiBank[] = [
  { en: 'Kasikornbank', th: 'ธนาคารกสิกรไทย', alias: ['kasikorn', 'kbank', 'กสิกร'] },
  { en: 'Bangkok Bank', th: 'ธนาคารกรุงเทพ', alias: ['bbl'] },
  { en: 'Siam Commercial Bank', th: 'ธนาคารไทยพาณิชย์', alias: ['scb'] },
  { en: 'Krungthai Bank', th: 'ธนาคารกรุงไทย', alias: ['ktb'] },
  { en: 'Bank of Ayudhya (Krungsri)', th: 'ธนาคารกรุงศรีอยุธยา', alias: ['krungsri', 'bay', 'กรุงศรี'] },
  { en: 'TMBThanachart Bank (ttb)', th: 'ธนาคารทหารไทยธนชาต', alias: ['ttb', 'tmb', 'thanachart', 'thai military', 'ทหารไทย', 'ธนชาต'] },
  { en: 'UOB Thailand', th: 'ธนาคารยูโอบี', alias: ['uob', 'united overseas'] },
  { en: 'Government Savings Bank', th: 'ธนาคารออมสิน', alias: ['gsb'] },
  { en: 'CIMB Thai Bank', th: 'ธนาคารซีไอเอ็มบี ไทย', alias: ['cimb', 'ซีไอเอ็มบี'] },
  { en: 'Kiatnakin Phatra Bank', th: 'ธนาคารเกียรตินาคินภัทร', alias: ['kiatnakin', 'kkp', 'เกียรตินาคิน'] },
  { en: 'Land and Houses Bank', th: 'ธนาคารแลนด์ แอนด์ เฮ้าส์', alias: ['land housing', 'lh', 'lhbank'] },
  { en: 'Thai Credit Bank', th: 'ธนาคารไทยเครดิต', alias: [] },
  { en: 'ICBC (Thai) Bank', th: 'ธนาคารไอซีบีซี (ไทย)', alias: ['industrial commercial china'] },
  { en: 'Standard Chartered Bank (Thai)', th: 'ธนาคารสแตนดาร์ดชาร์เตอร์ด (ไทย)', alias: [] },
  { en: 'Citibank', th: 'ธนาคารซิตี้แบงก์', alias: ['citi'] },
  { en: 'Bank for Agriculture and Agricultural Cooperatives (BAAC)', th: 'ธนาคารเพื่อการเกษตรและสหกรณ์การเกษตร', alias: ['baac', 'agriculture', 'ธกส'] },
  { en: 'Government Housing Bank', th: 'ธนาคารอาคารสงเคราะห์', alias: ['ghb', 'gh', 'ghbank', 'ธอส'] },
  { en: 'Islamic Bank of Thailand', th: 'ธนาคารอิสลามแห่งประเทศไทย', alias: ['islamic', 'ibank', 'อิสลาม'] },
];
/** The English names, in the list's order: the Bank dropdown's stored answers. */
export const THAI_BANK_NAMES: string[] = THAI_BANKS.map(b => b.en);
/** A dropdown entry's label: the English name and the Thai name, the same in every language. */
export const thaiBankLabel = (b: ThaiBank): string => `${b.en} / ${b.th}`;

// The branch and the rest: what follows a comma, a bracket, a dash between spaces, or 'สาขา' (branch).
const BRANCH = /[,(–—]|\s[-/]\s|สาขา/;
// The words every bank's name carries, in either language, and the small words between.
const GENERIC = /\b(?:bank|pcl|plc|public|company|co|ltd|limited|thailand|the|of|for|and)\b|ธนาคาร|จำกัด|มหาชน/g;
const NOT_LETTER = /[^\p{L}\p{M}\p{N}]+/gu;
const THAI = /[ก-๛]/;
/** The words a bank text is matched by: its part before the branch, in lower case, without the generic words. */
export const bankWords = (s: any): string[] =>
  String(s == null ? '' : s).toLowerCase().split(BRANCH)[0].replace(/[.']/g, '').replace(NOT_LETTER, ' ').replace(GENERIC, ' ').split(' ').filter(Boolean);
/** Do `words` begin with `key` — the key's letters, word by whole word ("krung" + "thai" is "krungthai")? */
const startsWithWords = (words: string[], key: string): boolean => {
  let s = '';
  for (const w of words) { s += w; if (s === key) return true; if (s.length >= key.length) return false; }
  return false;
};
type BankKey = { bank: ThaiBank; key: string; thai: boolean };
let keys: BankKey[] | null = null;
const bankKeys = (): BankKey[] => keys || (keys = THAI_BANKS.flatMap(bank =>
  [bank.en, bank.th, ...bank.alias].map(a => ({ bank, key: bankWords(a).join(''), thai: THAI.test(a) })).filter(k => k.key)));
/** The listed bank a text means (see above); null when none. The longest name or alias matched wins. */
export function thaiBankOf(typed: any): ThaiBank | null {
  const words = bankWords(typed);
  if (!words.length) return null;
  const joined = words.join('');
  let best: BankKey | null = null;
  for (const k of bankKeys()) {
    // A Thai name has no word boundaries to keep to: a Thai alias is matched as the text's start.
    const hit = startsWithWords(words, k.key) || (k.thai && joined.startsWith(k.key));
    if (hit && (!best || k.key.length > best.key.length)) best = k;
  }
  return best ? best.bank : null;
}
